import { useState } from 'react'
import { useAdminSettings, useUpdateAdminSettings } from '../lib/admin'
import { useToast } from '../components/ToastContainer'

export default function AdminSettings() {
  const { data: settings, isLoading, error } = useAdminSettings()
  const updateSettings = useUpdateAdminSettings()
  const { success: toastSuccess, error: toastError } = useToast()

  const [editAdminEmail, setEditAdminEmail] = useState(false)
  const [adminEmailInput, setAdminEmailInput] = useState(settings?.admin_email ?? '')
  const [errorText, setErrorText] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorText('')
    setIsSaving(true)
    try {
      await updateSettings.mutateAsync({ admin_email: adminEmailInput })
      setEditAdminEmail(false)
      toastSuccess('Email de notificacao atualizado com sucesso!')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorText(detail ?? 'Erro ao atualizar email de notificacao')
      toastError('Erro ao atualizar email de notificacao')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditAdminEmail(false)
    setAdminEmailInput(settings?.admin_email ?? '')
    setErrorText('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-xl font-bold text-[var(--color-text)]">Configuracoes</h2>
        <p className="mt-4 text-[var(--color-danger)]">Erro ao carregar configuracoes</p>
      </div>
    )
  }

  if (!settings) return null

  return (
    <div className="max-w-2xl space-y-6">
      <h2 className="text-xl font-bold text-[var(--color-text)]">Configuracoes</h2>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm space-y-4">
        <h3 className="text-lg font-semibold text-[var(--color-text)]">Estatisticas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total de Usuarios" value={settings.total_users} />
          <StatCard label="Pendentes" value={settings.pending_count} color="warning" />
          <StatCard label="Aprovados" value={settings.approved_count} color="success" />
          <StatCard label="Rejeitados" value={settings.rejected_count} color="danger" />
        </div>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm space-y-4">
        <h3 className="text-lg font-semibold text-[var(--color-text)]">Email de Notificacao</h3>
        <p className="text-sm text-[var(--color-text-muted)]">Email utilizado para enviar notificacoes do sistema.</p>

        <form onSubmit={handleSave} className="space-y-4">
          {editAdminEmail ? (
            <>
              <input
                type="email"
                value={adminEmailInput}
                onChange={(e) => setAdminEmailInput(e.target.value)}
                placeholder="admin@exemplo.com"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-subtle)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
              />
              {errorText && (
                <p className="text-sm text-[var(--color-danger)]">{errorText}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg bg-[var(--color-success)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-success)] disabled:opacity-50"
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]"
                >
                  Cancelar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[var(--color-text)] break-all">
                {settings.admin_email || 'Nao configurado'}
              </p>
              <button
                type="button"
                onClick={() => setEditAdminEmail(true)}
                className="text-sm text-[var(--color-brand-500)] hover:underline"
              >
                Editar
              </button>
            </>
          )}
        </form>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm space-y-4">
        <h3 className="text-lg font-semibold text-[var(--color-text)]">URL Base da Aplicacao</h3>
        <p className="text-sm text-[var(--color-text-muted)] break-all">{settings.app_base_url}</p>
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color?: 'warning' | 'success' | 'danger' }) {
  const colorMap: Record<string, string> = {
    warning: 'bg-[var(--color-warning-100)] text-[var(--color-warning-700)]',
    success: 'bg-[var(--color-success-100)] text-[var(--color-success-700)]',
    danger: 'bg-[var(--color-danger-100)] text-[var(--color-danger-700)]',
  }
  const cls = colorMap[color ?? ''] ?? 'bg-[var(--color-surface-subtle)] text-[var(--color-text)]'

  return (
    <div className={`rounded-lg px-3 py-4 text-center text-sm ${cls}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="mt-1">{label}</div>
    </div>
  )
}
